import { HelpIcon } from '@daily/shared/components/Icons/HelpIcon';
import { Menu } from '@daily/shared/components/Menu';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { useTheme } from '@daily/shared/contexts/Theme';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrderedParticipantIds } from '../../contexts/ParticipantsProvider';
import { robotsClassName } from '../../lib/robots';
import { useBreakoutSessionState } from '../BreakoutRooms/hooks/useBreakoutSessionState';
import { ToggleText } from './ToggleText';
import { TrayButton } from './TrayButton';

interface Props extends React.ComponentProps<typeof TrayButton> {}

export const ContactHostControls: React.FC<Props> = ({
  className,
  onClick,
  ...props
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const orderedParticipantIds = useOrderedParticipantIds();
  const {
    breakoutHost,
    breakoutStatus,
    isRequestingHelp,
    updateAskForHelpStatus,
  } = useBreakoutSessionState();
  const [showHelpMenu, setShowHelpMenu] = useState(false);

  const handleButtonClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    setShowHelpMenu(true);
    onClick?.(ev);
  };

  const handleMenuItemClick = () => {
    updateAskForHelpStatus(!isRequestingHelp);
    setShowHelpMenu(false);
  };

  const handleMenuClose = () => {
    setShowHelpMenu(false);
  };

  if (breakoutStatus !== 'ongoing') return null;

  const isHostInRoom = orderedParticipantIds.includes(breakoutHost);

  return (
    <div
      className={classnames('contact-host-controls', {
        active: isRequestingHelp,
      })}
    >
      <TrayButton
        className={classnames(className, {
          [robotsClassName('btn-contact-host')]: !isRequestingHelp,
          [robotsClassName('btn-cancel-contact-host')]: isRequestingHelp,
        })}
        disabled={isHostInRoom}
        onClick={handleButtonClick}
        {...props}
        id={props?.id ?? 'contact-host-btn'}
      >
        <HelpIcon
          className="icon"
          color={
            isRequestingHelp
              ? colors.system.white
              : isHostInRoom
              ? colors.supportiveText
              : 'currentColor'
          }
        />
        <ToggleText
          disabled={isHostInRoom}
          on={isRequestingHelp}
          onText={t('general.cancelWithEllipsis')}
          offText={t('breakouts.contactHostEllipsis')}
        />
      </TrayButton>
      {showHelpMenu && (
        <Menu
          align="left"
          aria-labelledby={props?.id ?? 'contact-host-btn'}
          id="contact-host-menu"
          items={[
            {
              label: (
                <Stack align="center" gap={4} horizontal>
                  <HelpIcon size={16} />
                  <Text variant="strong">
                    {isRequestingHelp
                      ? t('general.cancel')
                      : t('breakouts.contactHost')}
                  </Text>
                </Stack>
              ),
              static: true,
            },
            {
              label: isRequestingHelp
                ? t('breakouts.cancelRequestForHelp')
                : t('breakouts.askHostForHelp'),
              onClick: handleMenuItemClick,
            },
          ]}
          onClose={handleMenuClose}
          placement="top"
        />
      )}
      <style jsx>{`
        .contact-host-controls :global(.icon) {
          border-radius: 4px;
        }
        .contact-host-controls.active :global(.icon) {
          background: ${colors.system.red};
        }
      `}</style>
    </div>
  );
};
